import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DownCaratIcon } from 'foundations-assets/icons/components/DownCaratIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsInAppAuth } from '../../../v2/hooks/user/useUserInfo';
import CurrencyPicker from '../../common/CurrencyPicker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useGlobalNavHeight = () => {
  const isInAppAuth = useIsInAppAuth();
  const [height, setHeight] = useState(0);
  const measure = useCallback(() => {
    if (isInAppAuth) return;
    const globalNav = document.querySelector('.hs-global-nav-header');
    if (globalNav) {
      setHeight(globalNav.getBoundingClientRect().height);
    }
  }, [isInAppAuth]);
  useEffect(() => {
    measure();
    // Re-measure when global nav loads (injected async)
    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [measure]);
  return height;
};
const MobileSideNav = ({
  selectedNavItem,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleBarRef = useRef(null);
  const location = useLocation();
  const globalNavHeight = useGlobalNavHeight();

  // Close the nav when the route changes (user selected an item)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close on Escape key or click outside (e.g. global nav hamburger)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    const handleClickOutside = e => {
      const target = e.target;
      if (toggleBarRef.current && !toggleBarRef.current.contains(target) && !target.closest('#mobile-side-nav-list')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Prevent body scroll when overlay is open, preserving previous value
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Track toggle bar bottom position for overlay offset
  const [toggleBarBottom, setToggleBarBottom] = useState(0);
  const measureToggleBarBottom = useCallback(() => {
    if (toggleBarRef.current) {
      setToggleBarBottom(toggleBarRef.current.getBoundingClientRect().bottom);
    }
  }, []);
  useEffect(() => {
    if (!toggleBarRef.current) return;
    const observer = new ResizeObserver(measureToggleBarBottom);
    observer.observe(toggleBarRef.current);
    return () => observer.disconnect();
  }, [measureToggleBarBottom]);
  // Re-measure when the nav opens, since getBoundingClientRect is viewport-relative
  // and may be stale if the user scrolled before opening the nav
  useEffect(() => {
    if (isOpen) {
      measureToggleBarBottom();
    }
  }, [isOpen, measureToggleBarBottom]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      ref: toggleBarRef,
      as: "nav",
      "aria-label": I18n.text('pricingPage.sidenav.toggleMenu'),
      position: "sticky",
      insetBlockStart: `${globalNavHeight}px`,
      zIndex: 50,
      paddingInline: "500",
      paddingBlock: "100",
      marginInline: "-200",
      backgroundColor: "fill-surface-default",
      boxShadow: "200",
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Button, {
          use: "transparent",
          "aria-expanded": isOpen,
          "aria-controls": "mobile-side-nav-list",
          onClick: () => setIsOpen(prev => !prev),
          "data-test-id": "side-nav-mobile-toggle",
          paddingInlineStart: "0",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "200",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `pricingPage.pages.products.tabs.${selectedNavItem}.tabTitle`
              })
            }), /*#__PURE__*/_jsx(DownCaratIcon, {
              size: "100"
            })]
          })
        }), !isOpen && /*#__PURE__*/_jsx(View, {
          flexShrink: 0,
          maxInlineSize: "120px",
          children: /*#__PURE__*/_jsx(CurrencyPicker, {})
        })]
      })
    }), isOpen && /*#__PURE__*/_jsx(View, {
      id: "mobile-side-nav-list",
      role: "region",
      "aria-label": I18n.text('pricingPage.sidenav.toggleMenu'),
      position: "fixed",
      insetInlineStart: "0",
      insetInlineEnd: "0",
      insetBlockStart: `${toggleBarBottom}px`,
      insetBlockEnd: "0",
      backgroundColor: "fill-surface-default",
      overflowY: "auto",
      zIndex: 49,
      paddingInline: "300",
      paddingBlockStart: "300",
      paddingBlockEnd: "600",
      children: children
    })]
  });
};
export default MobileSideNav;